import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, catchError, debounceTime, distinctUntilChanged, forkJoin, of, switchMap, takeUntil } from 'rxjs';
import { MarketingCampaignApiService } from '../services/marketing-campaign-api.service';
import {
  CampaignExcludedContact,
  CampaignExcludedSegment,
  CampaignList,
  CampaignListSourceType,
  CampaignManualRecipient,
  CampaignPreviewCount,
  CampaignAttachment,
  MarketingCampaign,
  MarketingCampaignRequest,
  campaignStatusLabel,
} from '../models/marketing-campaign.model';
import { EmailChannelApiService } from '../services/email-channel-api.service';
import { ChannelType, EmailChannelView } from '../models/email-channel.model';
import { FileApiService } from '../../correos/services/file-api.service';
import { SegmentService } from '../../segments/services/segment-api.service';
import { Segment } from '../../segments/models/segment.model';
import { SegmentMembersModalComponent } from '../segment-members-modal/segment-members-modal.component';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { TemplatePickerModalComponent } from '../template-picker-modal/template-picker-modal.component';
import {
  ContactPickerMode,
  ContactPickerModalComponent,
} from '../contact-picker-modal/contact-picker-modal.component';
import { TemplateApiService } from '../../templates/services/template-api.service';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { Contact } from '../../contactos/models/contacto.model';
import {
  AvailableVariableNamespace,
  Template,
  TemplateAttachment,
} from '../../templates/models/template.model';
import {
  StarterTemplate,
} from '../../templates/grapes-email-editor/starter-templates';
import { StarterTemplateModalComponent } from '../../templates/starter-template-modal/starter-template-modal.component';
import { htmlToPlainText } from '../utils/html-to-text';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { RichTextEditorComponent } from '../../../shared/components/rich-text-editor/rich-text-editor.component';

type SaveMode = 'DRAFT' | 'SCHEDULE' | 'SEND_NOW';

/** Canales que admiten adjuntos (fuente de verdad para el caso override). */
const ATTACHMENT_CHANNELS = new Set<ChannelType>(['USER_GMAIL', 'USER_MICROSOFT', 'USER_SMTP']);

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * Un adjunto en el form de campaña. Mientras `uploading` es true el archivo
 * aún se está subiendo (chip con spinner); cuando termina se completa `fileId`.
 * Si la subida falla, `error` queda en true (chip rojo, no se envía). Los
 * adjuntos precargados al editar entran como ya-subidos (uploading:false).
 */
interface CampaignAttachmentItem {
  /** Clave local estable para *ngFor track. */
  key: number;
  name: string;
  size: number;
  uploading: boolean;
  error: boolean;
  // Disponibles cuando la subida terminó OK o vienen precargados:
  fileId?: number;
  originalName?: string;
  contentType?: string;
  fileSize?: number;
}

@Component({
  selector: 'app-campaign-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TemplatePickerModalComponent,
    ContactPickerModalComponent,
    StarterTemplateModalComponent,
    SegmentMembersModalComponent,
    HasPermissionDirective,
    RichTextEditorComponent,
  ],
  templateUrl: './campaign-form.component.html',
})
export class CampaignFormComponent implements OnInit, OnDestroy {
  private readonly api = inject(MarketingCampaignApiService);
  private readonly channelApi = inject(EmailChannelApiService);
  private readonly fileApi = inject(FileApiService);
  private readonly segmentApi = inject(SegmentService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly toast = inject(ToastService);
  private readonly templateApi = inject(TemplateApiService);
  private readonly contactApi = inject(ContactoApiService);

  starterModalOpen = false;

  private readonly previewRefresh$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private readonly overrideEmailSearch$ = new Subject<string>();

  /** Ancho simulado del editor del cuerpo del correo. */
  previewMode: 'desktop' | 'mobile' = 'desktop';

  /** Sube imágenes insertadas desde el editor de texto enriquecido del cuerpo. */
  readonly bodyImageUploadHandler = (file: File): Promise<string> =>
    this.templateApi.uploadAsset(file).toPromise().then(res => res!.url);

  /** Sube archivos adjuntos (PDF, Word, Excel, PPT, TXT, ZIP) insertados como link descargable. */
  readonly bodyAttachmentUploadHandler = (file: File): Promise<{ url: string; fileName: string }> =>
    this.templateApi.uploadDocument(file).toPromise().then(res => ({ url: res!.url, fileName: res!.fileName }));

  // Override de canal: email chip-input con autocomplete
  overrideEmailInput = '';
  overrideEmailSuggestions: Contact[] = [];
  overrideEmailsLoading = false;
  overrideEmailSuggestionsOpen = false;

  loading = true;
  saving = false;
  error: string | null = null;
  successMessage: string | null = null;

  isEditing = false;
  campaignId: number | null = null;
  campaign: MarketingCampaign | null = null;

  // Form fields. bodyText se auto-deriva del bodyHtml al guardar
  // (htmlToPlainText), por eso no está acá.
  form = {
    name: '',
    subject: '',
    preheader: '',
    bodyHtml: '',
    channelType: '' as ChannelType | '',
    channelRefId: null as number | null,
    scheduledAt: '' as string,    // ISO local "yyyy-MM-ddThh:mm" para input
  };

  showHtmlPreview = false;

  // Channels
  channels: EmailChannelView[] = [];
  channelMenuOpen = false;
  useBulkDefault = true; // si true, no se manda channelType/RefId

  // ── Adjuntos ──
  attachments: CampaignAttachmentItem[] = [];
  private attachmentKeySeq = 0;

  /**
   * Gate de adjuntos: ¿el canal de envío efectivo soporta adjuntos?
   *  - null  → aún sin resolver (mientras carga el endpoint del canal bulk):
   *            ocultamos la sección para evitar parpadeo.
   *  - true  → MUESTRA la sección (botón Adjuntar + chips).
   *  - false → OCULTA la sección y muestra la nota explicativa.
   */
  adjuntosSoportados: boolean | null = null;
  /** Canal efectivo (para el texto de la nota cuando no soporta). */
  gateEffectiveChannelType: string | null = null;
  private gateLoading = false;

  // Segments
  segments: Segment[] = [];
  campaignLists: CampaignList[] = [];
  segmentSelectorOpen = false;
  segmentSearchTerm = '';

  // Ver contactos de un segmento vinculado (modal con búsqueda + scroll infinito)
  segmentMembersModalOpen = false;
  membersModalSegmentId: number | null = null;
  membersModalSegmentName = '';

  // Manual recipients + exclusiones
  manualRecipients: CampaignManualRecipient[] = [];
  excludedContacts: CampaignExcludedContact[] = [];
  excludedSegments: CampaignExcludedSegment[] = [];
  excludedSegmentSelectorOpen = false;
  excludedSegmentSearchTerm = '';

  contactPickerOpen = false;
  contactPickerMode: ContactPickerMode = 'INCLUDE';

  preview: CampaignPreviewCount | null = null;
  previewLoading = false;

  excludeTab: 'CONTACTS' | 'SEGMENTS' = 'CONTACTS';

  // Plantilla aplicada (snapshot ya copiado a form.bodyHtml).
  // Se conserva una referencia al objeto solo para mostrar el chip
  // "Plantilla: X" en la UI; al guardar la campaña SOLO viaja el bodyHtml.
  selectedTemplate: Template | null = null;
  showTemplatePicker = false;
  templateWarnings: string[] = [];
  availableNamespaces: AvailableVariableNamespace[] = [];

  ngOnInit(): void {
    this.campaignId = Number(this.route.snapshot.paramMap.get('id')) || null;
    this.isEditing = !!this.campaignId;
    this.loadAll();
    this.previewRefresh$
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(() => this.fetchPreview());

    this.overrideEmailSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (!term || term.length < 2) {
            this.overrideEmailSuggestions = [];
            this.overrideEmailSuggestionsOpen = false;
            this.overrideEmailsLoading = false;
            return of(null);
          }
          this.overrideEmailsLoading = true;
          const businessId = this.auth.getBusinessId();
          if (!businessId) return of(null);
          return this.contactApi
            .searchContacts({ businessId, searchTerm: term, page: 0, size: 8 })
            .pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((res) => {
        this.overrideEmailsLoading = false;
        if (res) {
          const alreadyAdded = new Set(this.manualRecipients.map((r) => r.idContact));
          this.overrideEmailSuggestions = (res.content ?? []).filter(
            (c) => c.id !== undefined && !alreadyAdded.has(c.id!)
          );
          this.overrideEmailSuggestionsOpen = this.overrideEmailSuggestions.length > 0;
        } else {
          this.overrideEmailSuggestions = [];
          this.overrideEmailSuggestionsOpen = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Navega al editor dedicado de contenido. Requiere que la campaña ya
   * exista en backend (necesita id). Si todavía es una campaña nueva sin
   * guardar, primero guardamos como borrador y después navegamos.
   */
  async goEditContent(): Promise<void> {
    if (this.isReadOnly()) return;
    if (this.campaignId) {
      this.router.navigate(['/marketing-email/editar', this.campaignId, 'contenido']);
      return;
    }
    // Campaña nueva sin id → guardar borrador primero (validación laxa).
    const validation = this.validateForm('DRAFT');
    if (validation) {
      this.error = validation;
      return;
    }
    const req = this.buildRequest(null, 'DRAFT');
    this.saving = true;
    this.api.create(req).subscribe({
      next: (saved) => {
        this.campaign = saved;
        this.campaignId = saved.id;
        this.isEditing = true;
        this.saving = false;
        this.router.navigate(['/marketing-email/editar', saved.id, 'contenido']);
      },
      error: (err) => {
        this.saving = false;
        this.error = err?.error?.message || 'No se pudo guardar el correo antes de editar el contenido.';
      },
    });
  }

  private loadAll(): void {
    this.loading = true;
    forkJoin({
      channels: this.channelApi.listMine().pipe(catchError(() => of([] as EmailChannelView[]))),
      segments: this.segmentApi.getSegments(0, 500).pipe(catchError(() => of({ content: [], totalElements: 0, totalPages: 0, size: 0 } as any))),
      vars: this.templateApi
        .getAvailableVariables()
        .pipe(catchError(() => of([] as AvailableVariableNamespace[]))),
    }).subscribe(({ channels, segments, vars }) => {
      this.channels = channels || [];
      this.segments = segments?.content || [];
      this.availableNamespaces = vars || [];
      if (this.isEditing && this.campaignId) {
        this.loadCampaign(this.campaignId);
      } else {
        this.loading = false;
        // Campaña nueva: arranca en "Mi canal bulk" → resolver gate por endpoint.
        this.recomputeAttachmentGate();
      }
    });
  }

  private loadCampaign(id: number): void {
    forkJoin({
      campaign: this.api.getById(id),
      lists: this.api.listLists(id).pipe(catchError(() => of([] as CampaignList[]))),
      manualRecipients: this.api.listManualRecipients(id).pipe(catchError(() => of([] as CampaignManualRecipient[]))),
      excludedContacts: this.api.listExcludedContacts(id).pipe(catchError(() => of([] as CampaignExcludedContact[]))),
      excludedSegments: this.api.listExcludedSegments(id).pipe(catchError(() => of([] as CampaignExcludedSegment[]))),
      preview: this.api.previewCount(id).pipe(catchError(() => of(null as CampaignPreviewCount | null))),
    }).subscribe({
      next: ({ campaign, lists, manualRecipients, excludedContacts, excludedSegments, preview }) => {
        this.campaign = campaign;
        this.campaignLists = lists || [];
        this.manualRecipients = manualRecipients || [];
        this.excludedContacts = excludedContacts || [];
        this.excludedSegments = excludedSegments || [];
        this.preview = preview;
        this.form.name = campaign.name;
        this.form.subject = campaign.subject || '';
        this.form.preheader = campaign.preheader || '';
        this.form.bodyHtml = campaign.bodyHtml || '';
        if (campaign.channelType && campaign.channelRefId) {
          this.form.channelType = campaign.channelType as ChannelType;
          this.form.channelRefId = campaign.channelRefId;
          this.useBulkDefault = false;
        }
        this.form.scheduledAt = campaign.scheduledAt
          ? this.toLocalDatetimeString(campaign.scheduledAt)
          : '';
        // Precarga de adjuntos existentes (ya vienen subidos: storageRef→fileId).
        this.attachments = (campaign.attachments || []).map(a => ({
          key: this.attachmentKeySeq++,
          name: a.fileName,
          size: a.sizeBytes ?? 0,
          uploading: false,
          error: false,
          fileId: Number(a.storageRef),
          originalName: a.fileName,
          contentType: a.mimeType ?? undefined,
          fileSize: a.sizeBytes ?? undefined,
        }));
        // Resolver el gate según el canal cargado (override local o bulk endpoint).
        this.recomputeAttachmentGate();
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.message || 'No se pudo cargar el correo.';
        this.loading = false;
      },
    });
  }

  private fetchPreview(): void {
    if (!this.campaignId) return;
    this.previewLoading = true;
    this.api.previewCount(this.campaignId)
      .pipe(catchError(() => of(null as CampaignPreviewCount | null)), takeUntil(this.destroy$))
      .subscribe(res => {
        this.preview = res;
        this.previewLoading = false;
      });
  }

  private triggerPreview(): void {
    this.previewRefresh$.next();
  }

  // ===== Channel selector =====
  toggleChannelMenu(): void { this.channelMenuOpen = !this.channelMenuOpen; }

  selectChannel(c: EmailChannelView | null): void {
    if (c === null) {
      this.useBulkDefault = true;
      this.form.channelType = '';
      this.form.channelRefId = null;
    } else {
      if (!c.isConnected) return;
      this.useBulkDefault = false;
      this.form.channelType = c.channelType as ChannelType;
      this.form.channelRefId = c.channelRefId;
    }
    this.channelMenuOpen = false;
    this.recomputeAttachmentGate();
  }

  /** Radio "Override de canal": activa override y recalcula el gate. */
  onSelectOverride(): void {
    this.useBulkDefault = false;
    this.recomputeAttachmentGate();
  }

  // ===== Gate de adjuntos =====

  /**
   * Determina `adjuntosSoportados` según el canal de envío elegido:
   *  - OVERRIDE (no useBulkDefault): evalúa ESE channelType localmente contra
   *    {USER_GMAIL, USER_MICROSOFT, USER_SMTP}. Si aún no eligió canal, sin gate.
   *  - BULK DEFAULT (useBulkDefault): consulta el endpoint del canal bulk
   *    efectivo y usa su `attachmentsSupported`.
   * Mientras se resuelve el endpoint, deja `adjuntosSoportados = null` (oculto).
   */
  private recomputeAttachmentGate(): void {
    if (!this.useBulkDefault) {
      // Override: evaluación local inmediata.
      const type = this.form.channelType;
      if (!type) {
        // Aún no eligió canal del override → ocultar todo (null), sin nota dura
        // (la nota "no admite adjuntos" sería confusa sin canal elegido).
        this.adjuntosSoportados = null;
        this.gateEffectiveChannelType = null;
        return;
      }
      this.gateEffectiveChannelType = type;
      this.adjuntosSoportados = ATTACHMENT_CHANNELS.has(type as ChannelType);
      return;
    }

    // Bulk default: resolver vía endpoint (oculto mientras carga).
    this.adjuntosSoportados = null;
    this.gateEffectiveChannelType = null;
    this.gateLoading = true;
    this.channelApi.getBulkAttachmentSupport()
      .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe(res => {
        this.gateLoading = false;
        if (!res) {
          // No se pudo resolver: ocultamos la sección (no bloqueamos el form).
          this.adjuntosSoportados = false;
          this.gateEffectiveChannelType = null;
          return;
        }
        this.adjuntosSoportados = res.attachmentsSupported;
        this.gateEffectiveChannelType = res.effectiveChannelType;
      });
  }

  // ===== Adjuntos =====

  onSeleccionarArchivos(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;
    for (let i = 0; i < files.length; i++) {
      this.subirArchivo(files[i]);
    }
    input.value = '';
  }

  private subirArchivo(file: File): void {
    if (file.size > MAX_FILE_BYTES) {
      this.toast.error({
        title: 'Archivo muy grande',
        message: `"${file.name}" supera el límite de 10 MB.`,
      });
      return;
    }

    const att: CampaignAttachmentItem = {
      key: this.attachmentKeySeq++,
      name: file.name,
      size: file.size,
      uploading: true,
      error: false,
    };
    this.attachments.push(att);

    this.fileApi.uploadFile(file).subscribe({
      next: (res) => {
        att.uploading = false;
        att.fileId = res.fileId;
        att.originalName = res.originalName;
        att.contentType = res.contentType;
        att.fileSize = res.fileSize;
      },
      error: (err) => {
        att.uploading = false;
        att.error = true;
        const msg = err?.error?.message || `No se pudo subir "${file.name}".`;
        this.toast.error({ title: 'Error al adjuntar', message: msg });
      },
    });
  }

  quitarAdjunto(att: CampaignAttachmentItem): void {
    this.attachments = this.attachments.filter(a => a.key !== att.key);
  }

  /** Hay al menos un adjunto subiéndose en este momento. */
  get hayAdjuntosSubiendo(): boolean {
    return this.attachments.some(a => a.uploading);
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /** Adjuntos subidos OK → shape del request (storageRef = String(fileId)). */
  private buildAttachments(): CampaignAttachment[] {
    return this.attachments
      .filter(a => !a.uploading && !a.error && a.fileId !== undefined)
      .map(a => ({
        fileName: a.originalName || a.name,
        mimeType: a.contentType || undefined,
        sizeBytes: a.fileSize ?? a.size,
        storageRef: String(a.fileId),
      }));
  }

  // ===== Override email input =====
  onOverrideEmailInput(): void {
    this.overrideEmailSearch$.next(this.overrideEmailInput.trim());
  }

  selectOverrideContact(contact: Contact): void {
    this.overrideEmailInput = '';
    this.overrideEmailSuggestions = [];
    this.overrideEmailSuggestionsOpen = false;

    if (!this.campaignId) {
      this.error = 'Guarda primero el correo como borrador para agregar destinatarios.';
      return;
    }
    if (contact.id === undefined) return;
    const alreadyAdded = this.manualRecipients.some((r) => r.idContact === contact.id);
    if (alreadyAdded) return;

    this.api.addManualRecipients(this.campaignId, [contact.id]).subscribe({
      next: () => {
        this.reloadManualRecipients();
        this.toast.success({ title: 'Destinatario agregado', message: contact.fullname || contact.mail || 'Contacto agregado.' });
      },
      error: (err) => {
        this.error = err?.error?.message || 'No se pudo agregar el contacto.';
      },
    });
  }

  /** Formato de email válido (validación laxa, solo forma; el backend revalida). */
  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /** true si lo escrito es un email con formato válido y no supera 320 chars. */
  get canAddOverrideEmail(): boolean {
    const val = this.overrideEmailInput.trim();
    return val.length > 0 && val.length <= 320 && this.isValidEmail(val);
  }

  /**
   * Agrega el texto escrito como destinatario directo por email arbitrario,
   * esté o no registrado como contacto (Inc 27). No exige contacto: valida
   * solo el formato del email.
   */
  addOverrideEmail(): void {
    const email = this.overrideEmailInput.trim();
    if (!this.canAddOverrideEmail) {
      this.error = 'Ingresa un correo con formato válido (ej: nombre@dominio.com).';
      return;
    }
    if (!this.campaignId) {
      this.error = 'Guarda primero el correo como borrador para agregar destinatarios.';
      return;
    }
    const alreadyAdded = this.manualRecipients.some(
      (r) => r.email.toLowerCase() === email.toLowerCase()
    );
    if (alreadyAdded) {
      this.overrideEmailInput = '';
      this.overrideEmailSuggestions = [];
      this.overrideEmailSuggestionsOpen = false;
      return;
    }
    this.error = null;
    this.api.addManualEmails(this.campaignId, [{ email }]).subscribe({
      next: (summary) => {
        this.overrideEmailInput = '';
        this.overrideEmailSuggestions = [];
        this.overrideEmailSuggestionsOpen = false;
        this.reloadManualRecipients();
        if (summary.alreadyPresent && !summary.added) {
          this.toast.info({ title: 'Ya estaba', message: `${email} ya era destinatario.` });
        } else {
          this.toast.success({ title: 'Destinatario agregado', message: email });
        }
      },
      error: (err) => {
        const msg = err?.error?.message || 'No se pudo agregar el correo.';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  private reloadManualRecipients(): void {
    if (!this.campaignId) return;
    this.api
      .listManualRecipients(this.campaignId)
      .pipe(catchError(() => of(this.manualRecipients)))
      .subscribe((list) => {
        this.manualRecipients = list;
        this.triggerPreview();
      });
  }

  closeOverrideSuggestions(): void {
    setTimeout(() => {
      this.overrideEmailSuggestionsOpen = false;
    }, 150);
  }

  get activeChannel(): EmailChannelView | undefined {
    if (this.useBulkDefault) return undefined;
    return this.channels.find(c =>
      c.channelType === this.form.channelType && c.channelRefId === this.form.channelRefId);
  }

  channelLabel(type: string | undefined | null): string {
    if (!type) return '';
    const map: Record<string, string> = {
      USER_GMAIL: 'Gmail', USER_MICROSOFT: 'Outlook',
      USER_SMTP: 'SMTP', USER_API: 'API', SYSTEM_API: 'Sistema',
    };
    return map[type] || type;
  }

  // ===== Segment selector =====
  toggleSegmentSelector(): void {
    this.segmentSelectorOpen = !this.segmentSelectorOpen;
    if (this.segmentSelectorOpen) this.refreshSegments();
  }

  /**
   * Relee los segmentos al abrir un selector: la lista se cargaba una sola vez al entrar al
   * correo, así que un segmento pasado a borrador en otra pestaña se seguía ofreciendo.
   */
  private refreshSegments(): void {
    this.segmentApi.getSegments(0, 500).pipe(catchError(() => of(null))).subscribe(page => {
      if (page) this.segments = page.content || [];
    });
  }

  /**
   * Solo los segmentos «Activo» (READY) se ofrecen para adjuntar o excluir: un borrador no
   * es una lista válida. `segments` se queda completo para que un segmento ya vinculado que
   * luego se inactivó siga mostrando su nombre.
   */
  private isSelectableSegment(s: Segment): boolean {
    return s.status === 'READY';
  }

  get availableSegments(): Segment[] {
    const linkedIds = new Set(this.campaignLists.map(l => l.idContactSegment));
    let list = this.segments.filter(s => this.isSelectableSegment(s) && !linkedIds.has(s.id));
    if (this.segmentSearchTerm) {
      const t = this.segmentSearchTerm.toLowerCase();
      list = list.filter(s => s.name.toLowerCase().includes(t));
    }
    return list;
  }

  attachSegment(seg: Segment): void {
    if (!this.campaignId) {
      this.error = 'Guarda primero el correo como borrador para adjuntar segmentos.';
      return;
    }
    const sourceType: CampaignListSourceType = seg.type === 'DYNAMIC' ? 'DYNAMIC' : 'STATIC';
    this.api.addList(this.campaignId, { segmentId: seg.id, sourceType }).subscribe({
      next: (entry) => {
        this.campaignLists = [...this.campaignLists, entry];
        this.segmentSelectorOpen = false;
        this.segmentSearchTerm = '';
        this.triggerPreview();
      },
      error: (err) => {
        this.error = err?.error?.message || 'No se pudo vincular el segmento.';
      },
    });
  }

  detachSegment(list: CampaignList): void {
    if (!this.campaignId) return;
    this.api.removeList(this.campaignId, list.idContactSegment).subscribe({
      next: () => {
        this.campaignLists = this.campaignLists.filter(l => l.id !== list.id);
        this.triggerPreview();
      },
      error: (err) => {
        this.error = err?.error?.message || 'No se pudo desvincular el segmento.';
      },
    });
  }

  // ===== Manual recipients & exclusiones =====
  get availableExcludedSegments(): Segment[] {
    const linkedIds = new Set(this.excludedSegments.map(l => l.idContactSegment));
    let list = this.segments.filter(s => this.isSelectableSegment(s) && !linkedIds.has(s.id));
    if (this.excludedSegmentSearchTerm) {
      const t = this.excludedSegmentSearchTerm.toLowerCase();
      list = list.filter(s => s.name.toLowerCase().includes(t));
    }
    return list;
  }

  toggleExcludedSegmentSelector(): void {
    this.excludedSegmentSelectorOpen = !this.excludedSegmentSelectorOpen;
    if (this.excludedSegmentSelectorOpen) this.refreshSegments();
  }

  openContactPicker(mode: ContactPickerMode): void {
    if (!this.campaignId) {
      this.error = 'Guarda primero el correo como borrador para adjuntar contactos.';
      return;
    }
    this.contactPickerMode = mode;
    this.contactPickerOpen = true;
  }

  closeContactPicker(): void {
    this.contactPickerOpen = false;
  }

  get pickerAlreadyAttachedIds(): number[] {
    if (this.contactPickerMode === 'EXCLUDE') {
      return this.excludedContacts.map(c => c.idContact);
    }
    // Los emails sueltos no tienen contactId → se descartan para el picker de contactos.
    return this.manualRecipients
      .map(c => c.idContact)
      .filter((id): id is number => id !== null);
  }

  onContactPickerConfirm(contactIds: number[]): void {
    if (!this.campaignId || contactIds.length === 0) {
      this.contactPickerOpen = false;
      return;
    }
    const campaignId = this.campaignId;
    const mode = this.contactPickerMode;

    const apply = (mode === 'EXCLUDE')
      ? this.api.addExcludedContacts(campaignId, contactIds)
      : this.api.addManualRecipients(campaignId, contactIds);

    apply.subscribe({
      next: (summary) => {
        this.contactPickerOpen = false;
        if (mode === 'EXCLUDE') {
          this.api.listExcludedContacts(campaignId)
            .pipe(catchError(() => of(this.excludedContacts)))
            .subscribe(list => {
              this.excludedContacts = list;
              this.triggerPreview();
            });
        } else {
          this.api.listManualRecipients(campaignId)
            .pipe(catchError(() => of(this.manualRecipients)))
            .subscribe(list => {
              this.manualRecipients = list;
              this.triggerPreview();
            });
        }
        const summaryMsg = `${summary.added} agregado${summary.added === 1 ? '' : 's'}`
          + (summary.alreadyPresent ? `, ${summary.alreadyPresent} ya estaba${summary.alreadyPresent === 1 ? '' : 'n'}` : '')
          + (summary.skipped ? `, ${summary.skipped} omitido${summary.skipped === 1 ? '' : 's'}` : '');
        this.toast.info({
          title: mode === 'EXCLUDE' ? 'Exclusiones agregadas' : 'Contactos agregados',
          message: summaryMsg,
        });
      },
      error: (err) => {
        this.error = err?.error?.message || 'No se pudieron agregar los contactos.';
      },
    });
  }

  removeManualRecipient(r: CampaignManualRecipient): void {
    if (!this.campaignId) return;
    // DELETE por id de fila: sirve para emails sueltos (idContact null) y contactos.
    this.api.removeManualRecipientById(this.campaignId, r.id).subscribe({
      next: () => {
        this.manualRecipients = this.manualRecipients.filter(x => x.id !== r.id);
        this.triggerPreview();
        this.toast.deleted('Destinatario');
      },
      error: (err) => {
        const msg = err?.error?.message || 'No se pudo quitar el destinatario.';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  removeExcludedContact(c: CampaignExcludedContact): void {
    if (!this.campaignId) return;
    this.api.removeExcludedContact(this.campaignId, c.idContact).subscribe({
      next: () => {
        this.excludedContacts = this.excludedContacts.filter(x => x.id !== c.id);
        this.triggerPreview();
      },
      error: (err) => {
        this.error = err?.error?.message || 'No se pudo quitar la exclusión.';
      },
    });
  }

  attachExcludedSegment(seg: Segment): void {
    if (!this.campaignId) return;
    const sourceType: CampaignListSourceType = seg.type === 'DYNAMIC' ? 'DYNAMIC' : 'STATIC';
    this.api.addExcludedSegment(this.campaignId, { segmentId: seg.id, sourceType }).subscribe({
      next: (entry) => {
        this.excludedSegments = [...this.excludedSegments, entry];
        this.excludedSegmentSelectorOpen = false;
        this.excludedSegmentSearchTerm = '';
        this.triggerPreview();
      },
      error: (err) => {
        this.error = err?.error?.message || 'No se pudo excluir el segmento.';
      },
    });
  }

  detachExcludedSegment(entry: CampaignExcludedSegment): void {
    if (!this.campaignId) return;
    this.api.removeExcludedSegment(this.campaignId, entry.idContactSegment).subscribe({
      next: () => {
        this.excludedSegments = this.excludedSegments.filter(x => x.id !== entry.id);
        this.triggerPreview();
      },
      error: (err) => {
        this.error = err?.error?.message || 'No se pudo quitar la exclusión.';
      },
    });
  }

  segmentName(idSegment: number): string {
    const s = this.segments.find(x => x.id === idSegment);
    return s ? s.name : `Segmento #${idSegment}`;
  }

  segmentTotal(idSegment: number): number {
    const s = this.segments.find(x => x.id === idSegment);
    return s ? s.totalContacts : 0;
  }

  /**
   * Estado legible de un segmento vinculado que ya no está activo (p. ej. «Borrador»); null si
   * está activo o no se conoce. El envío ignora los segmentos incluidos que no están activos.
   */
  inactiveSegmentLabel(idSegment: number): string | null {
    const s = this.segments.find(x => x.id === idSegment);
    if (!s || s.status === 'READY' || s.status === 'CALCULATING') return null;
    const labels: Record<string, string> = {
      DRAFT: 'Borrador', ERROR: 'Error', SUPPRESSED: 'Suprimido', ARCHIVED: 'Archivado',
    };
    return labels[s.status] ?? s.status;
  }

  /** Abre el modal con los contactos que componen un segmento vinculado. */
  openSegmentMembers(idSegment: number): void {
    this.membersModalSegmentId = idSegment;
    this.membersModalSegmentName = this.segmentName(idSegment);
    this.segmentMembersModalOpen = true;
  }

  closeSegmentMembers(): void {
    this.segmentMembersModalOpen = false;
  }

  /**
   * Total estimado a enviar. Prefiere `preview.finalEstimate` (cómputo real
   * post-dedup post-exclusión que devuelve el backend). Fallback: suma de los
   * totalContacts de los segmentos vinculados.
   */
  get totalEstimatedContacts(): number {
    if (this.preview) return this.preview.finalEstimate;
    return this.campaignLists
      .filter(l => !this.inactiveSegmentLabel(l.idContactSegment))
      .reduce((sum, l) => sum + this.segmentTotal(l.idContactSegment), 0);
  }

  // ===== Save / Launch =====
  /**
   * Validación según modo:
   *  - DRAFT: solo `name` es required. Permite ir y volver entre secciones
   *    sin tener subject/cuerpo todavía.
   *  - SCHEDULE / SEND_NOW: además exige subject + bodyHtml (y SCHEDULE,
   *    scheduledAt — pero ese check vive en `save()`).
   */
  validateForm(mode: SaveMode = 'DRAFT'): string | null {
    if (!this.form.name.trim()) return 'El nombre es obligatorio.';
    if (this.form.name.length > 200) return 'El nombre no puede exceder 200 caracteres.';
    if ((this.form.preheader || '').length > 255) return 'El preheader no puede exceder 255 caracteres.';
    if (this.form.subject && this.form.subject.length > 998) {
      return 'El asunto no puede exceder 998 caracteres.';
    }
    if (mode === 'DRAFT') return null;

    // Para programar o enviar ya: contenido completo obligatorio.
    if (!this.form.subject?.trim()) return 'El asunto es obligatorio para programar o enviar.';
    const bodyTrimmed = this.form.bodyHtml?.trim() ?? '';
    const isPlaceholder = bodyTrimmed === '<!-- borrador: contenido pendiente -->';
    if (!bodyTrimmed || isPlaceholder) {
      return 'El cuerpo del correo es obligatorio para programar o enviar. Usa "Editar contenido" para agregar el HTML del correo.';
    }

    // Al editar una campaña existente, debe tener al menos un destinatario.
    if (this.isEditing && this.campaignLists.length === 0 && this.manualRecipients.length === 0) {
      return 'Debes agregar al menos un segmento o contacto antes de enviar o programar el correo.';
    }

    return null;
  }

  buildRequest(scheduledAtIso: string | null, mode: SaveMode = 'DRAFT'): MarketingCampaignRequest {
    let html = (this.form.bodyHtml || '').trim();
    // Si guardamos DRAFT sin cuerpo, mandamos un placeholder mínimo. El backend
    // marca `bodyHtml` como required cuando no hay templateCode; este HTML
    // comment cumple el constraint pero renderea vacío. El usuario lo
    // reemplaza al usar "Editar contenido", "Comenzar desde plantilla" o
    // "Usar plantilla guardada".
    if (mode === 'DRAFT' && !html) {
      html = '<!-- borrador: contenido pendiente -->';
    }
    const bodyText = html ? htmlToPlainText(html) : null;
    const req: MarketingCampaignRequest = {
      name: this.form.name.trim(),
      subject: this.form.subject?.trim() || (mode === 'DRAFT' ? '(borrador)' : null),
      preheader: this.form.preheader || null,
      bodyHtml: html || null,
      bodyText: bodyText || null,
      templateCode: null,
      channelType: this.useBulkDefault ? null : (this.form.channelType || null),
      channelRefId: this.useBulkDefault ? null : (this.form.channelRefId || null),
      scheduledAt: scheduledAtIso,
    };
    // Adjuntos (REPLACE-ALL): incluir SOLO cuando la sección está visible y el
    // canal los soporta (el usuario realmente los gestionó). Si la sección está
    // oculta por el gate, OMITIMOS `attachments` para no borrar inadvertidamente
    // los existentes. Si está visible y quitó todos → buildAttachments() = [] → borra.
    if (this.adjuntosSoportados === true) {
      req.attachments = this.buildAttachments();
    }
    return req;
  }

  /** true mientras no se pueda guardar/enviar (guardando o con subidas en curso). */
  get guardarDeshabilitado(): boolean {
    return this.saving || this.hayAdjuntosSubiendo;
  }

  saveDraft(): void { this.save('DRAFT'); }
  schedule(): void { this.save('SCHEDULE'); }

  /** true si hay una fecha/hora de programación elegida y ya quedó en el pasado. */
  get isScheduledDateInPast(): boolean {
    if (!this.form.scheduledAt) return false;
    const local = new Date(this.form.scheduledAt);
    if (isNaN(local.getTime())) return false;
    return local <= new Date();
  }

  /**
   * Inc 42: true cuando hay una fecha de programación FUTURA válida. En ese caso
   * "Programar" es la acción primaria y "Lanzar correo" se deshabilita: pulsarlo
   * enviaba al instante un correo que el usuario acababa de programar.
   */
  get hasFutureSchedule(): boolean {
    return !!this.form.scheduledAt && !this.isScheduledDateInPast;
  }

  /** Tooltip de "Lanzar correo" — explica por qué está deshabilitado (Inc 42). */
  get lanzarAhoraTitle(): string {
    return this.hasFutureSchedule
      ? 'Hay un envío programado. Quita la fecha para lanzar ahora.'
      : 'Lanzar el correo ahora';
  }

  /** Fecha/hora de programación legible en es-PE (para el toast del Inc 43). */
  private formatScheduledAt(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('es-PE', { dateStyle: 'long', timeStyle: 'short' });
  }
  async launchNow(): Promise<void> {
    const ok = await this.confirmDialog.confirm({
      title: 'Lanzar correo',
      message: '¿Lanzar el correo ahora? Una vez lanzado no se podrá editar ni cancelar.',
      confirmText: 'Lanzar'
    });
    if (!ok) return;
    this.save('SEND_NOW');
  }

  private save(mode: SaveMode): void {
    this.error = null;
    this.successMessage = null;
    const validation = this.validateForm(mode);
    if (validation) { this.error = validation; return; }

    let scheduledAtIso: string | null = null;
    if (mode === 'SCHEDULE') {
      if (!this.form.scheduledAt) {
        this.error = 'Selecciona fecha y hora de programación.';
        return;
      }
      const local = new Date(this.form.scheduledAt);
      if (isNaN(local.getTime())) {
        this.error = 'Fecha inválida.';
        return;
      }
      if (local <= new Date()) {
        this.error = 'La fecha de programación debe ser posterior al momento actual.';
        return;
      }
      scheduledAtIso = local.toISOString();
    }
    // En modo DRAFT: scheduledAt se preserva si existía, o queda null.
    if (mode === 'DRAFT' && this.form.scheduledAt) {
      const local = new Date(this.form.scheduledAt);
      if (!isNaN(local.getTime())) scheduledAtIso = local.toISOString();
    }

    const req = this.buildRequest(scheduledAtIso, mode);
    this.saving = true;

    const action$ = this.isEditing && this.campaignId
      ? this.api.update(this.campaignId, req)
      : this.api.create(req);

    action$.subscribe({
      next: (saved) => {
        this.campaign = saved;
        this.campaignId = saved.id;
        this.isEditing = true;

        if (mode === 'SEND_NOW') {
          this.api.sendNow(saved.id).subscribe({
            next: (summary) => {
              this.saving = false;
              this.successMessage = `Correo encolado. ${summary.queued}/${summary.totalRecipients} contactos.`;
              setTimeout(() => this.router.navigate(['/marketing-email', saved.id]), 800);
            },
            error: (err) => {
              this.saving = false;
              // 400 sin canal usable: la campaña NO pasa a SENDING. Mostramos el
              // mensaje del backend (en español) como toast, no como envío simulado.
              const msg = err?.error?.message || 'Error al lanzar el correo.';
              this.error = msg;
              this.toast.error({ title: 'No se pudo enviar', message: msg });
            },
          });
        } else if (mode === 'SCHEDULE') {
          this.saving = false;
          // Inc 43: programar confirma con toast y devuelve al usuario a la Lista.
          // Antes se quedaba en el form (o iba a /editar/:id) sin señal de que el
          // envío había quedado agendado.
          const cuando = this.formatScheduledAt(scheduledAtIso);
          this.toast.success({
            title: 'Correo programado',
            message: cuando ? `Correo programado para el ${cuando}.` : 'Correo programado.',
          });
          this.router.navigate(['/marketing-email']);
        } else {
          this.saving = false;
          this.successMessage = 'Borrador guardado.';
          if (!this.route.snapshot.paramMap.get('id')) {
            // DRAFT: navegar a edición para poder adjuntar segmentos.
            this.router.navigate(['/marketing-email/editar', saved.id]);
          }
        }
      },
      error: (err) => {
        this.saving = false;
        const raw: string = err?.error?.message || '';
        // El backend solo bloquea la edición en SENDING/SENT (Borrador,
        // Programado y En revisión SÍ se pueden editar). Mensaje en español.
        const isNotEditable = raw.includes('Cannot edit a campaign in status')
          || raw.includes('ya fue enviado')
          || raw.includes('se está enviando');
        if (isNotEditable) {
          // Mostramos el mensaje del backend (ya viene en español) y refrescamos
          // el estado real de la campaña para reflejar por qué se bloqueó.
          this.error = raw || 'No se puede editar un correo que ya fue enviado o se está enviando.';
          if (this.campaignId) {
            this.api.getById(this.campaignId).subscribe({
              next: (c) => { this.campaign = c; },
              error: () => {},
            });
          }
        } else {
          this.error = raw || 'Error al guardar el correo.';
        }
      },
    });
  }

  cancel(): void {
    this.router.navigate(['/marketing-email']);
  }

  // ===== Plantilla =====

  openTemplatePicker(): void {
    if (this.isReadOnly()) return;
    this.showTemplatePicker = true;
  }

  closeTemplatePicker(): void {
    this.showTemplatePicker = false;
  }

  /**
   * Aplica una plantilla al formulario.
   * - Snapshot del htmlContent → form.bodyHtml.
   * - Si el subject del form está vacío, también copia tpl.subject.
   * - Detecta merge tags huérfanas (no presentes en el catálogo) y avisa.
   */
  async onTemplateSelected(tpl: Template): Promise<void> {
    this.showTemplatePicker = false;

    // Si el body actual ya tiene contenido, confirmar antes de sobreescribir.
    if (this.form.bodyHtml?.trim()) {
      const ok = await this.confirmDialog.confirm({
        title: 'Reemplazar contenido',
        message:
          'Ya tenés HTML en el cuerpo del correo. ¿Reemplazarlo con la plantilla seleccionada?',
        confirmText: 'Reemplazar',
      });
      if (!ok) return;
    }

    this.selectedTemplate = tpl;
    this.form.bodyHtml = tpl.htmlContent ?? '';
    if (!this.form.subject?.trim()) {
      this.form.subject = tpl.subject ?? '';
    }
    this.templateWarnings = this.detectOrphanMergeTags(
      `${this.form.subject ?? ''} ${this.form.bodyHtml ?? ''}`
    );

    // Copiar los adjuntos de la plantilla al correo (shape 1:1 storageRef=fileId
    // string). Dedup por storageRef para no acumular copias si se aplica dos veces.
    const addedAttachments = this.applyTemplateAttachments(tpl.attachments ?? []);
    if (addedAttachments > 0 && this.adjuntosSoportados === false) {
      const chan = this.gateEffectiveChannelType
        ? ` (${this.gateEffectiveChannelType})`
        : '';
      this.templateWarnings = [
        ...this.templateWarnings,
        `El canal de envío actual${chan} no admite adjuntos, así que los ${addedAttachments} archivo(s) de la plantilla no se enviarán con este correo.`,
      ];
    }

    const adjuntosNota =
      addedAttachments > 0 ? ` (${addedAttachments} adjunto(s) incluidos).` : '.';
    this.successMessage = `Plantilla "${tpl.name}" aplicada al cuerpo del correo${adjuntosNota}`;
    setTimeout(() => (this.successMessage = null), 4000);
  }

  /**
   * Agrega los adjuntos de la plantilla a `this.attachments`, evitando duplicar
   * los que ya estén presentes (mismo storageRef/fileId). Devuelve cuántos se
   * agregaron efectivamente.
   */
  private applyTemplateAttachments(tplAttachments: TemplateAttachment[]): number {
    const existingIds = new Set(
      this.attachments.map((a) => a.fileId).filter((id): id is number => id != null)
    );
    let added = 0;
    for (const a of tplAttachments) {
      const fileId = Number(a.storageRef);
      if (existingIds.has(fileId)) continue;
      existingIds.add(fileId);
      this.attachments.push({
        key: this.attachmentKeySeq++,
        name: a.fileName,
        size: a.sizeBytes ?? 0,
        uploading: false,
        error: false,
        fileId,
        originalName: a.fileName,
        contentType: a.mimeType ?? undefined,
        fileSize: a.sizeBytes ?? undefined,
      });
      added++;
    }
    return added;
  }

  removeTemplate(): void {
    this.selectedTemplate = null;
    this.templateWarnings = [];
    // No tocamos form.bodyHtml — el usuario podría querer conservar lo que ya
    // editó después de aplicar la plantilla.
  }

  private detectOrphanMergeTags(text: string): string[] {
    if (!text) return [];
    const declared = new Set(
      this.availableNamespaces.flatMap((ns) => ns.variables.map((v) => v.key))
    );
    const re = /\{\{\s*([a-zA-Z_][\w.]*)\s*\}\}/g;
    const found = new Set<string>();
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) found.add(m[1]);
    const orphans = [...found].filter((k) => !declared.has(k));
    if (!orphans.length) return [];
    return [
      `Variables sin catálogo: ${orphans.join(', ')}. ` +
        'Se reemplazarán por vacío en cada destinatario al enviar.',
    ];
  }

  // ===== Helpers =====
  private toLocalDatetimeString(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  statusLabel(status: string | null | undefined): string {
    return campaignStatusLabel(status);
  }

  isReadOnly(): boolean {
    if (!this.campaign) return false;
    return this.campaign.status !== 'DRAFT' && this.campaign.status !== 'SCHEDULED';
  }

  togglePreview(): void { this.showHtmlPreview = !this.showHtmlPreview; }

  openStarterModal(): void {
    if (this.isReadOnly()) return;
    this.starterModalOpen = true;
  }

  onStarterPicked(tpl: StarterTemplate): void {
    this.starterModalOpen = false;
    this.form.bodyHtml = tpl.html;
    // Limpia el chip de plantilla guardada (si había), ya no es esa.
    this.selectedTemplate = null;
  }
}
